import React from 'react';
import classNames from 'classnames/bind';
import { Link } from 'react-router-dom';
import { Row, Col, HStack } from '@saucelabs/sl-component-grid';
import { Typography } from '@saucelabs/ds-typography';

import { getProductType } from '../../records/Plan';
import {
  CREDITS_PER_BLOCK,
  INTERVAL_TO_LABEL,
  PLAN_INTERVAL,
  PRODUCT_API_TESTING,
  PRODUCT_MOBILE_QUALITY,
  ROUTE_PARENT_SECTION,
  ROUTE_SECTION_PURCHASE,
  TESTING_TYPE_TO_TIER,
} from '../../constants';
import { projectsToMonthlyExecutions } from '../../utils/plans';
import { amountSeparator, formatThousand } from '../../utils/numbers';
import { Title } from './Title';
import { PlanDetails } from './PlanDetails';
import { SauceFeature } from './SauceFeature';
import Styles from './SummaryTile.styles';
import { ProductAmount } from './ProductAmount';
import { utils as plansUtils } from '../../ducks/plans';

const context = classNames.bind(Styles);

interface Props {
  currentCredits?: number,
  currentSubscription?: Subscription,
  editPlanLink?: boolean,
  subscriptionPreview: SubscriptionPreview,
}

const PlanTile: React.FC<Props> = ({
  currentCredits,
  currentSubscription,
  editPlanLink = false,
  subscriptionPreview,
}) => {
  const { interval, monthlyRate, periodRate, plan, productAmount } = subscriptionPreview;

  const productType = getProductType(plan);
  const isMobileQuality = productType === PRODUCT_MOBILE_QUALITY;
  const isApiTesting = productType === PRODUCT_API_TESTING;

  // The only special case where we present amounts per year instead of per month:
  const isUsageAnnual = plan.isUsage() && interval === PLAN_INTERVAL.YEAR;

  // In most cases the monthly rate is displayed, but sometimes we want to display annual rate
  const periodRateDisplayed = isUsageAnnual ? periodRate : monthlyRate;

  // Upgrade stuff
  const isAlreadySubscribed = !!currentSubscription;
  const isUsageUpgrade = plan.isUsage() && isAlreadySubscribed;
  const currentSubscriptionInterval: IntervalUnit = currentSubscription?.getInterval();

  const currentSubscriptionCredits = plansUtils.creditsConsideringAnnualBonus(
    currentSubscription?.getCreditBlocksAmount() * CREDITS_PER_BLOCK,
    currentSubscriptionInterval === PLAN_INTERVAL.YEAR,
  );
  const bullets = currentSubscriptionInterval === interval ? [
    `You will receive an additional ${formatThousand(subscriptionPreview.productAmount - currentSubscriptionCredits, 'standard')} credits today, which will expire in one ${interval}.`,
    `You will be charged only $${formatThousand(subscriptionPreview.periodRate - currentSubscription?.final_price, 'standard')} + taxes and fees today.`,
    `Starting on your next billing day (${currentSubscription?.displayNextBillDate() as string}) you will receive ${formatThousand(subscriptionPreview.productAmount, 'standard')} credits/month.`,
  ] : [
    `You will receive an additional ${formatThousand(subscriptionPreview.productAmount, 'standard')} credits today, which will expire in one ${interval}.`,
    `You will be charged $${formatThousand(subscriptionPreview.periodRate, 'standard')} + taxes and fees today.`,
    `Your remaining credits (${formatThousand(currentCredits, 'standard')}) will be used first.`,
    `Your next billing day will be on ${currentSubscription?.displayNextBillDate() as string}. You will receive ${formatThousand(subscriptionPreview.productAmount, 'standard')} credits/${interval}.`,
  ];

  return (
    <div className={context('plan-tile')}>
      <Title
        isLegacy={!plan.isUnified() && !plan.isUsage()}
        name={isAlreadySubscribed ? `Current Plan: ${plan.displayName() as string}` : plan.displayName()}
      >
        {editPlanLink && (
          <Link
            className={context('edit-plan-link')}
            to={`/${ROUTE_PARENT_SECTION}/${ROUTE_SECTION_PURCHASE}`}
          >
            Edit Plan
          </Link>
        )}
      </Title>
      <Row between="xs">
        <Col xs={9}>
          {!plan.isUsage() && (
            <SauceFeature
              featureName={(isApiTesting || isMobileQuality) ?
                TESTING_TYPE_TO_TIER[plan.testing_type] :
                undefined}
              isMonthly={interval === PLAN_INTERVAL.MONTH}
            />
          )}
          <PlanDetails price={plan.isUsage() ? undefined : amountSeparator(monthlyRate)}>
            {productAmount && !isMobileQuality && (
              <ProductAmount
                interval={interval}
                productType={productType}
                secondaryValue={isApiTesting ?
                  projectsToMonthlyExecutions(productAmount, plan.add_ons) :
                  undefined}
                value={productAmount}
              />
            )}
          </PlanDetails>
          {plan.isUsage() && (
            <p className="mt-1 leading-5 font-normal text-gray-600 text-sm">
              When you have fewer than 1,000 credits remaining within a billing cycle,
              {` ${formatThousand(CREDITS_PER_BLOCK, 'standard')} credits will automatically be added to your account `}
              so you never run out of test capacity. You’ll be charged $50 plus taxes and fees.
            </p>
          )}
        </Col>
        <Col
          className="text-right"
          xs={3}
        >
          <HStack
            gap={1}
            justifyContent="flex-end"
          >
            <span className={context('text-grey-accent')}>
              {isUsageAnnual ? 'Annual' : 'Monthly'}
              {' Rate:'}
            </span>
            <strong className={context('text-lg', 'text-primary-dark')}>
              {amountSeparator(periodRateDisplayed)}
            </strong>
          </HStack>
          <div className={context('text-grey-primary')}>
            {`${INTERVAL_TO_LABEL[interval]} Billing`}
          </div>
        </Col>
      </Row>
      {isUsageUpgrade && (
        <Row>
          <Col xs={12}>
            <hr className="mt-1 mb-2 border-t-0 text-gray-200" />
            <Typography
              className="text-gray-900"
              disableTextColor
              variant="heading-6"
            >
              {'You are upgrading from '}
              {formatThousand(currentSubscriptionCredits, 'standard')}
              {currentSubscriptionInterval !== interval ? ` credits/${currentSubscriptionInterval}` : ''}
              {' to '}
              {formatThousand(subscriptionPreview.productAmount, 'standard')}
              {` credits/${interval}:`}
            </Typography>
            <ul className="m-0 p-0 pl-2">
              {bullets.map((text) => (
                <li
                  key={/^\w+/.exec(text)}
                  className="pt-0.5 leading-7 font-normal"
                >
                  {text}
                </li>
              ))}
            </ul>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default PlanTile;
export {
  PlanTile,
};
